import type { Doc } from "building-types/doc";
import type { RoomHandle } from "building-types/handles/docEntityHandleTypes";
import { RoomOutlineProblem } from "building-types/rooms/checkRoomOutline";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import intRange from "common/utils/intRange";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { snakeCaseToSentenceCase } from "common/utils/strings";
import inspectorRoomTypes from "doc/rooms/inspectorRoomTypes";
import { ROOM_BASE_NAME, roomClassBaseOffset } from "doc/rooms/roomAttributesTools";
import { defaultRoomAttributes } from "doc/rooms/roomCatalog";
import { docBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { BuildingGenerationContext } from "doc/utils/buildingGenerationContext";
import { aabb3AddPoint } from "geom/aabb3";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { planeXYAtZ, polyLine2OnPlane } from "geom/plane";
import {
  polyLine2IsSelfIntersecting,
  polyLine2Area,
  polyLine2WindingDir,
  polyLine2IsDegenerated,
  polyLine2EdgeCount,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3AABB3 } from "geom/polyLine3";

const MIN_ROOM_AREA = 5;

export function checkRoomOutline(polyline: PolyLine2, minRoomArea: number = MIN_ROOM_AREA): RoomOutlineProblem | false {
  if (polyLine2IsSelfIntersecting(polyline, true, DIST_EPSILON, DIST_EPSILON)) {
    return RoomOutlineProblem.SELF_INTERSECTS;
  }

  if (polyLine2Area(polyline) < minRoomArea) {
    return RoomOutlineProblem.AREA_TOO_SMALL;
  }

  if (polyLine2WindingDir(polyline) === WindingDir.CW) {
    return RoomOutlineProblem.CW_WINDING;
  }

  if (polyLine2IsDegenerated(polyline)) {
    return RoomOutlineProblem.DEGENERATED;
  }

  return false;
}

export type RoomOptions = Omit<
  Room,
  "entityType" | "edgeIds" | "attributes" | "name" | "docEntityHandle" | "roomHandle" | "pickHandle"
> &
  Partial<Room>;

export function roomNew(options: RoomOptions): Room {
  if (!(polyLine2WindingDir(options.outline) === WindingDir.CCW)) {
    console.assert(false, `room winding direction is reversed. roomdId=${options.id}`);
  }
  if (!(options.outline !== undefined)) {
    console.assert(false, `No room outline supplied. roomId=${options.id}`);
  }
  if (!(options.type !== undefined)) {
    console.assert(false, `No room type supplied. roomId=${options.id}`);
  }
  if (!(options.level !== undefined)) {
    console.assert(false, `No room level supplied. roomId=${options.id}`);
  }
  if (!(options.level?.id !== undefined)) {
    console.assert(false, `No room levelId supplied. roomId=${options.id}`);
  }
  if (!(options.parentId !== undefined)) {
    console.assert(false, `No room blockId supplied. roomId=${options.id}`);
  }

  const attributes = defaultRoomAttributes(options.type);

  const roomHandle: RoomHandle = {
    type: "ROOM",
    id: options.id,
  };

  // calculate extents of the room based on the floor assembly base offset and the ceiling height
  const roomClass = options.attributes?.roomClass;
  const base: WorldInches = worldInchesNew(
    options.level.floorElevation + (!!roomClass ? roomClassBaseOffset(roomClass, options.level) : 0),
  );
  const ceilingHeight = options.level.topOfWall;

  const aabb3 = polyLine3AABB3(polyLine2OnPlane(planeXYAtZ(base), options.outline));
  aabb3AddPoint(aabb3, aabb3.minX, aabb3.minY, aabb3.minZ + ceilingHeight);

  BuildingGenerationContext.indexAddNode(roomHandle, aabb3);

  return {
    ...options,
    ...docBuildingEntityBaseNew("ROOM", options.id, roomHandle),
    name: options.name ?? options.id,
    edgeIds: options.edgeIds ?? intRange(0, polyLine2EdgeCount(options.outline)).map((_, i) => i.toString()),
    attributes: { ...attributes, ...options.attributes },
    roomHandle,
    // A room generated inside a component instance is selected as the instance.
    ...(options.instanceId ? { pickHandle: { type: "COMPONENT_INSTANCE", id: options.instanceId } } : {}),
  };
}

export const MATCH_ROOM_TYPES = new RegExp(
  `^(?:${ROOM_BASE_NAME}|${inspectorRoomTypes.map((n) => n.replace(/_/g, " ")).join("|")})( |$)`,
  "ig",
);

export function roomHasBaseName(room?: Pick<Room, "name">): boolean {
  return roomNameHasBaseName(room?.name ?? "");
}

export function roomNameHasBaseName(name: Room["name"]): boolean {
  return !!name && name.match(MATCH_ROOM_TYPES) !== null;
}

export function roomAutoName(roomName: string, newType: string): string {
  if (roomNameHasBaseName(roomName)) {
    return snakeCaseToSentenceCase(roomName.replace(MATCH_ROOM_TYPES, `${newType}$1`));
  }
  return roomName;
}

export interface RoomTypeCounts {
  bathroomCount: number;
  bedroomCount: number;
  garageCount: number;
}

const bedroomRoomTypes = new Set<RoomType>([
  RoomType.MASTER_BED,
  RoomType.MASTER_SUITE,
  RoomType.BEDROOM,
  RoomType.GUEST_SUITE,
]);
const bathroomRoomTypes = new Set<RoomType>([
  RoomType.MASTER_BATH,
  RoomType.BATHFULL,
  RoomType.BATHFULL_1_DOOR,
  RoomType.BATHHALF,
  RoomType.JJ_BATH,
  RoomType.BED_BATH,
  RoomType.WATER_CLOSET,
  RoomType.POWDER,
]);
const garageRoomTypes = new Set<RoomType>([RoomType.GARAGE, RoomType.GARAGE_DETACH]);

export function aggregateCountsByRoomType(rooms: StaticRoom[]): RoomTypeCounts {
  const counts: RoomTypeCounts = {
    bathroomCount: 0,
    bedroomCount: 0,
    garageCount: 0,
  };

  rooms.forEach((room) => {
    if (room.attributes?.bedroomCount !== undefined) {
      counts.bedroomCount += room.attributes.bedroomCount;
    } else if (bedroomRoomTypes.has(room.type)) {
      counts.bedroomCount += 1;
    }

    if (room.attributes?.bathroomCount !== undefined) {
      counts.bathroomCount += room.attributes.bathroomCount;
    } else if (bathroomRoomTypes.has(room.type)) {
      counts.bathroomCount += 1;
    }

    if (room.attributes?.garageCount !== undefined) {
      counts.garageCount += room.attributes.garageCount;
    } else if (garageRoomTypes.has(room.type)) {
      counts.garageCount += 1;
    }
  });

  return counts;
}

/**
 * Get the approximate area of a room or rooms in square feet.
 * This is a rough estimate based on the room outline and is not guaranteed to be accurate.
 *
 * True area calculation is slightly different based on room type and wall thickness.
 */
export function docGetApproximateRoomArea(doc: Doc, handles: RoomHandle | RoomHandle[]): number {
  const roomHandles = Array.isArray(handles) ? handles : [handles];
  const area = roomHandles.reduce((area, handle) => {
    const room = doc.rooms[handle.id];
    if (!room) {
      return area;
    }
    return polyLine2Area(room.outline) + area;
  }, 0);
  return Math.floor(area / 144);
}
